import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useLazyQuery } from '@apollo/react-hooks';
import { Modal, Button, Text, VSpacer } from '@mqd/volt-base';

const GET_KYC_UPLOAD_URL = gql`
  query kycUploadUrl($cardholder_token: String!, $filename: String!) {
    kycUploadUrl(cardholder_token: $cardholder_token, filename: $filename)
  }
`;

const GET_KYB_UPLOAD_URL = gql`
  query kybUploadUrl($cardholder_token: String!, $filename: String!) {
    kybUploadUrl(cardholder_token: $cardholder_token, filename: $filename)
  }
`;

const KycDocumentUploadModal = ({ 
  isVisible, 
  hideModal, 
  cardholderToken, 
  onUploadSuccess,
  onUploadError,
  type,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const [getUploadUrl, { loading: urlLoading }] = useLazyQuery(type === 'kyb' ? GET_KYB_UPLOAD_URL : GET_KYC_UPLOAD_URL, {
    onCompleted: async (data) => {
      if ((data?.kycUploadUrl || data?.kybUploadUrl) && selectedFile) {
        await uploadFileToS3(data?.kycUploadUrl || data?.kybUploadUrl, selectedFile);
      }
    },
    onError: (error) => {
      console.error('Error getting upload URL:', error);
      setUploading(false);
      setError(error?.message);
      onUploadError && onUploadError(error);
    }
  });

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const uploadFileToS3 = async (uploadUrl, file) => {
    try {
      setUploading(true);
      setError(null); // Clear any previous errors
      
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'x-amz-tagging': 'client=NILE'
        },
      });

      if (response.ok) {
        console.log('File uploaded successfully');
        onUploadSuccess && onUploadSuccess(file.name);
        handleClose();
      } else {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file. Please try again.');
      onUploadError && onUploadError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = () => {
    if (selectedFile && cardholderToken) {
      getUploadUrl({
        variables: {
          cardholder_token: cardholderToken,
          filename: selectedFile.name
        }
      });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploading(false);
    setError(null);
    hideModal();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isUploadDisabled = !selectedFile || uploading || urlLoading;

  if (!isVisible) return null;

  return (
    <Modal
      heading="Upload document"
      hideModal={handleClose}
      width={500}
      footerButtons={[
        <Button
          key="upload"
          mod="primary"
          onClick={handleUpload}
          disabled={isUploadDisabled}
          loading={uploading || urlLoading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      ]}
    >
      <div>
        <Text type="h6">Select Document</Text>
        <Text>Choose a file to upload.</Text>
        <VSpacer factor={2} />
        
        {error && (
          <>
            <div style={{
              padding: '12px',
              backgroundColor: '#ffeaea',
              borderRadius: '4px',
              border: '1px solid #ff6b6b',
              marginBottom: '16px'
            }}>
              <div style={{ color: '#d63031', fontWeight: 'bold' }}>
                Error
              </div>
              <div style={{ color: '#d63031' }}>
                {error}
              </div>
            </div>
          </>
        )}
        
        <div 
          style={{ 
            border: `2px dashed ${isDragOver ? '#007bff' : '#ccc'}`,
            borderRadius: '8px',
            padding: '24px',
            textAlign: 'center',
            backgroundColor: isDragOver ? '#f8f9fa' : '#fafafa',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleFileSelect}
        >
          <Text style={{ color: isDragOver ? '#007bff' : '#666' }}>
            {selectedFile ? selectedFile.name : 'Drag and drop a file here, or click to browse'}
          </Text>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        
        {selectedFile && (
          <>
            <VSpacer factor={2} />
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <Text>
                Name: {selectedFile.name}
              </Text>
              <Text>
                Size: {formatFileSize(selectedFile.size)}
              </Text>
              <Text>
                Type: {selectedFile.type || 'Unknown'}
              </Text>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

KycDocumentUploadModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  hideModal: PropTypes.func.isRequired,
  cardholderToken: PropTypes.string.isRequired,
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  type: PropTypes.oneOf(['kyc', 'kyb']).isRequired,
};

KycDocumentUploadModal.defaultProps = {
  onUploadSuccess: () => {},
  onUploadError: () => {},
};

export default KycDocumentUploadModal;
